import type { Action } from 'kbar';
import type { Project } from 'types/api.types';
import type { NavigateFunction } from 'react-router';
import { useNavigate } from 'react-router';
import { type ProjectSettingsMenuKey, useProjectSettingsMenuKeysCallback } from 'hooks/use-project-settings-menu-keys';
import capitalize from 'lodash/capitalize';
import flatMap from 'lodash/flatMap';
import { useCuratedProjectsTree } from 'stores/rq/projects';
import { useMemo } from 'react';
import TemplateIcon from 'assets/images/template-icon.svg';
import SettingsIcon from 'assets/images/settings-icon.svg';
import EnvironmentIcon from 'assets/images/environment-icon.svg';
import VariableIcon from 'assets/images/variables-icon.svg';

import { useHasProjectPermissionCallback } from 'hooks/use-has-permission';
import compact from 'lodash/compact';
import { getProjectNameHierarchy } from 'utils/sub-project.utils';

const MAX_PROJECT_PATH_DISPLAY_LENGTH = 60;
const truncateMiddle = (value: string) => {
  if (!value || value.length <= MAX_PROJECT_PATH_DISPLAY_LENGTH) return value;

  const ellipsis = '...';
  const available = MAX_PROJECT_PATH_DISPLAY_LENGTH - ellipsis.length;
  const head = Math.floor(available / 2);
  const tail = available - head;

  return `${value.slice(0, head)}${ellipsis}${value.slice(-tail)}`;
};

const generateSettingsAction = (
  { id, displayName, baseKeywords }: Pick<Project, 'id'> & { displayName: string; baseKeywords: string },
  menuKeys: readonly ProjectSettingsMenuKey[],
  navigate: (path: string) => void
): Action[] => [
  {
    id: `PROJECT_${id}_GOTO_settings_PAGE`,
    parent: `PROJECT_${id}_SECTION`,
    name: `Go to Settings`,
    keywords: `Go to settings ${baseKeywords}`,
    section: displayName,
    icon: SettingsIcon
  },
  ...menuKeys.map(key => ({
    id: `PROJECT_${id}_GOTO_settings_PAGE_${key}`,
    parent: `PROJECT_${id}_GOTO_settings_PAGE`,
    name: capitalize(key),
    keywords: `${key} ${baseKeywords}`,
    perform: () => navigate(`/p/${id}/settings/${key}`),
    section: 'settings'
  }))
];

const generateProjectPageAction = (
  id: string,
  displayName: string,
  page: string,
  icon: string,
  navigate: NavigateFunction,
  baseKeywords: string
) => ({
  id: `PROJECT_${id}_GOTO_${page}_PAGE`,
  parent: `PROJECT_${id}_SECTION`,
  name: `Go to ${capitalize(page)}`,
  keywords: `Go to ${page} ${baseKeywords}`,
  perform: () => navigate(`/p/${id}/${page}`),
  icon,
  section: displayName
});

export default function useProjectNavigation() {
  const navigate = useNavigate();
  const { activeProjects: projectsMap, isLoading, isFetched, error, refetch } = useCuratedProjectsTree();
  const { hasProjectPermission, isLoading: isLoadingPermissions } = useHasProjectPermissionCallback();
  const { getMenuKeys: getSettingsMenuKeys, isLoading: isLoadingSettingsMenuKeys } =
    useProjectSettingsMenuKeysCallback();
  const activeProjects = useMemo(() => Object.values(projectsMap), [projectsMap]);
  const isLoadingProjects = isLoading || isLoadingPermissions || isLoadingSettingsMenuKeys;
  const isProjectsReady = isFetched && !isLoadingProjects;

  const actions = useMemo(
    () =>
      compact(
        flatMap(activeProjects, ({ id, name }) => {
          const displayName = getProjectNameHierarchy(id, projectsMap) ?? name;
          const truncatedDisplayName = truncateMiddle(displayName);
          const baseKeywords = `${name} ${id} ${displayName}`;
          const canViewProject = hasProjectPermission(id, 'VIEW_PROJECT');
          const settingsMenuKeys = getSettingsMenuKeys(id);

          return [
            {
              id: `PROJECT_${id}_SECTION`,
              name: truncatedDisplayName,
              keywords: baseKeywords,
              section: 'projects',
              priority: 2
            },
            generateProjectPageAction(
              id,
              truncatedDisplayName,
              'environments',
              EnvironmentIcon,
              navigate,
              baseKeywords
            ),
            canViewProject &&
              generateProjectPageAction(id, truncatedDisplayName, 'templates', TemplateIcon, navigate, baseKeywords),
            canViewProject &&
              generateProjectPageAction(id, truncatedDisplayName, 'variables', VariableIcon, navigate, baseKeywords),
            ...(settingsMenuKeys.length > 0
              ? generateSettingsAction(
                  { id, displayName: truncatedDisplayName, baseKeywords },
                  settingsMenuKeys,
                  navigate
                )
              : [])
          ];
        })
      ),
    [activeProjects, getSettingsMenuKeys, hasProjectPermission, navigate, projectsMap]
  );

  // A tree error leaves isProjectsReady true over an empty map (react-query keeps isFetched true and
  // the data default kicks in) — deliberately so, because flipping it false would lock the palette on
  // its loader. Consumers surface projectsError instead.
  return { actions, isLoadingProjects, isProjectsReady, projectsError: error, refetchProjects: refetch };
}
